import { createTheme, Theme, ThemeProvider } from '@material-ui/core/styles';
import createNcrRetailTheme, {
  createColorBlindNcrRetailTheme,
  defaultThemeColors,
} from '@ncr-retail/mui-theme';
import React, { createContext, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import tinyColor from 'tinycolor2';
import { CashierPreferenceLevel } from './common/enumerations/cashier-preference-level';
import { typographyOverrides } from './common/helpers/dimension-helper';
import { CashierPreferences } from './common/interfaces/dtos/CashierPreferences';
import {
  ITouchpointColorPalette,
  ITouchpointTheme,
} from './common/interfaces/dtos/ITouchpointTheme';
import { AppState } from './common/redux/configure-store';
import {
  defaultThemeColor,
  deuteranopiaThemeColor,
  protanopiaThemeColor,
  setTheme,
  themeColor,
  tritanopiaThemeColor,
} from './mui-theme';
import logo from 'src/common/media/ncr-logo-eps.svg';

const calculateTheme = (theme: Theme, userPreferences: CashierPreferences) => {
  theme.direction = userPreferences.leftHanded ? 'rtl' : 'ltr';
  let colorPalette = defaultThemeColor;
  let colorThemeFunc = createColorBlindNcrRetailTheme;

  switch (userPreferences.contrastLevel) {
    case CashierPreferenceLevel.One: {
      colorPalette = deuteranopiaThemeColor;
      break;
    }
    case CashierPreferenceLevel.Two: {
      colorPalette = protanopiaThemeColor;
      break;
    }
    case CashierPreferenceLevel.Three: {
      colorPalette = tritanopiaThemeColor;
      break;
    }
    default: {
      colorThemeFunc = createNcrRetailTheme;
    }
  }

  const updatedTheme = createTheme(colorThemeFunc(colorPalette), typographyOverrides());
  setTheme(colorPalette);

  return updatedTheme;
};

const calculateColorConfigurationOverrides = (
  colorConfig: ITouchpointColorPalette,
): ThemeConfigColors => {
  const { primaryColor, secondaryColor } = colorConfig;
  const primaryText = tinyColor(primaryColor).isLight() ? themeColor.black : themeColor.white;
  const secondaryText = tinyColor(secondaryColor).isLight() ? themeColor.black : themeColor.white;

  return {
    primaryColor,
    primaryText,
    secondaryColor,
    secondaryText,
  };
};

export const initialTheme: Theme = createTheme(
  createNcrRetailTheme(defaultThemeColors()),
  typographyOverrides(),
  {
    mainLogo: logo,
    configColors: {
      primaryColor: themeColor.primaryMain,
      primaryText: tinyColor(themeColor.primaryMain).isLight()
        ? themeColor.black
        : themeColor.white,
      secondaryColor: themeColor.secondaryMain,
      secondaryText: tinyColor(themeColor.secondaryMain).isLight()
        ? themeColor.black
        : themeColor.white,
    },
  },
);

export const ThemeContext = createContext<ThemeContextData>({
  theme: initialTheme,
  clearTheme: () => {},
  setTouchpointTheme: () => {},
});

export const EmeraldThemeProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const [theme, setTheme] = useState(initialTheme);
  const [emeraldTheme, setEmeraldTheme] = useState<ITouchpointTheme>();

  const userPreferences = useSelector((state: AppState) => state.cashierPreferences);

  const setTouchpointTheme = useCallback((touchpointTheme: ITouchpointTheme) => {
    setEmeraldTheme(touchpointTheme);
  }, []);

  const clearTheme = useCallback(() => {
    setTheme(initialTheme);
  }, []);

  useEffect(() => {
    setTheme(previousTheme => {
      return {
        ...previousTheme,
        ...calculateTheme(previousTheme, userPreferences),
        ...{ mainLogo: emeraldTheme ? emeraldTheme.logo : initialTheme.mainLogo },
        ...{
          configColors: emeraldTheme
            ? calculateColorConfigurationOverrides(emeraldTheme.colorPallete)
            : initialTheme.configColors,
        },
      };
    });
  }, [setTheme, userPreferences, emeraldTheme]);

  return (
    <ThemeContext.Provider value={{ theme, emeraldTheme, clearTheme, setTouchpointTheme }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};

export interface ThemeContextData {
  theme: Theme;
  emeraldTheme?: ITouchpointTheme;
  setTouchpointTheme: (touchpointTheme: ITouchpointTheme) => void;
  clearTheme: () => void;
}

interface ThemeConfigColors {
  primaryColor: string;
  primaryText: string;
  secondaryColor: string;
  secondaryText: string;
}

export type ConfigurableTheme = Theme;
